import { getDocument } from '@atlaskit/browser-apis';

const scrollableOverflowValues = new Set(['auto', 'scroll', 'overlay']);
const SCROLL_RANGE_EPSILON = 1;

/**
 * The `<Main>` landmark rendered by `@atlaskit/navigation-system`. Matched by
 * role rather than by id because ADS generates that id at runtime.
 */
export const MAIN_CONTENT_SELECTOR = 'main, [role="main"]';

const canScrollVertically = (element: HTMLElement): boolean =>
	element.scrollHeight - element.clientHeight > SCROLL_RANGE_EPSILON;

/**
 * True when `element` itself declares a scrollable overflow and currently has
 * somewhere to scroll to. Callers holding a candidate element (rather than a
 * descendant of one) need this because `getNearestScrollContainer` only
 * inspects ancestors.
 */
export const isVerticalScrollContainer = (element: HTMLElement | null): boolean => {
	const win = getDocument()?.defaultView;
	if (!element || !win) {
		return false;
	}
	const styles = win.getComputedStyle(element);
	const hasScrollableOverflow =
		scrollableOverflowValues.has(styles.overflowY) || scrollableOverflowValues.has(styles.overflow);

	return hasScrollableOverflow && canScrollVertically(element);
};

export const getNearestScrollContainer = (node: HTMLElement | null): HTMLElement | null => {
	const doc = getDocument();
	const win = doc?.defaultView;
	if (!node || !doc || !win) {
		return null;
	}

	let parent = node.parentElement;
	while (parent && parent !== doc.body && parent !== doc.documentElement) {
		if (isVerticalScrollContainer(parent)) {
			return parent;
		}

		parent = parent.parentElement;
	}

	return null;
};

/**
 * The element that scrolls the microsite's page content. Usually ADS's
 * `<Main>`, which carries the `overflow-y: auto` instead of the document, but
 * the walk above it keeps this working if ADS moves the overflow onto a
 * wrapper. Returns `null` when the document itself is the scroller, such as
 * a standalone host without the microsite shell.
 */
export const getPageScrollContainer = (): HTMLElement | null => {
	const main = getDocument()?.querySelector<HTMLElement>(MAIN_CONTENT_SELECTOR) ?? null;
	if (!main) {
		return null;
	}

	return isVerticalScrollContainer(main) ? main : getNearestScrollContainer(main);
};
